/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { emptyObject } from '@constants/empty';
import PlatformBatchCard from '@comp/BatchCard/PlatFormBatchCard';
import SingleBatchCardSection from '@cont/BatchGroup/SingleBatchCardSection';
import { useIsMobile } from '@stores/AppCommonData';
import { useSelector } from 'react-redux';
import Topics from '@cont/Syllabus/HomePage/Topics';
import H4 from '@base/H4/H4';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  EDUCATOR_PROFILE,
  SEE_ALL_CLICKED,
  SYLLABUS_CARD_CLICKED
} from '@constants/segment';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import DppCardSkeleton from '@skeletons/Dpp/DppCardSkeleton';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import useScrollToDPPSection from '@api/hooks/profile/useScrollToDPPSection';
import pushRoute from '@utils/pushRoute';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import Button from 'aquilla/core/molecules/Button';
import CloudUpload from '@icon/CloudDownload';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useRouter } from 'next/router';
import { getLink as getBatchLink } from '@models/Batch';
import segmentStorage from '@events/segmentStorage';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import DppCard from './Practices/AquillaDppCard';
import { addEducatorInLocalStorage, formatClassDetail } from './utils';
import {
  DPPS,
  PlusGoal,
  FreeCourses,
  TOPICS,
  SpecialClass,
  Batches,
  EducatorDpp
} from './constants';
import EducatorLegendsBanner from './EducatorLegendsBanner';
import BatchWrapper from './BatchWrapper';
import CourseCard from './CourseCard';
import useBrochureDownload from './hooks/useBrochureDownload';

const CarouselWrapper = styled.div`
  margin: ${Spacings.SPACING_10B} 0 0;
  ${({ $isDpp }) => {
    if ($isDpp) {
      return `
        .MuiIconButton-root {
        top: calc(50% - ${Spacings.SPACING_9B});
        }
      `;
    }
  }}
`;

const StyledPlatformBatch = styled(PlatformBatchCard)`
  .batch-thumbnail {
    height: 152px;
  }
}`;

const StyH4 = styled(H4)`
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const TopicContainer = styled.div`
  margin-top: ${Spacings.SPACING_11B};
  padding-bottom: ${Spacings.SPACING_9B};

  ${mediaQuery.uptoMobile} {
    margin: 0 ${Spacings.SPACING_4B};
  }
`;

const StyledButton = styled(Button)`
  [class*='button--right-icon'] {
    display: flex;
  }
`;

const StyledShowCase = styled(ShowCase)`
  [class*='aquilla-batchcard'] {
    padding-bottom: 62px;
    position: relative;
    height: 100%;
    > *:last-child {
      > *:last-child {
        position: absolute;
        bottom: 0;
        width: 100%;
      }
    }
  }
`;

const CardWrapper = styled.div``;

const BatchCard = ({ batch, username, _index, label }) => {
  const router = useRouter();
  const { handleDownload } = useBrochureDownload();

  const handleClick = () => {
    segmentStorage.setItem('lpss', 'Batches - Educator Profile Home');
    segmentStorage.setItem('lps', EDUCATOR_PROFILE);
    router.push(getBatchLink(batch));
  };
  const actionButtons = [];
  if (batch.brochure)
    actionButtons.push(
      <StyledButton
        startIcon={<CloudUpload color="white" width="24" height="24" />}
        size="medium"
        variant="primary"
        label="Download brochure"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload(batch.brochure);
        }}
      />
    );
  else
    actionButtons.push(
      <StyledButton size="medium" variant="secondary" label="Batch details" />
    );
  return (
    <BatchWrapper
      batchUID={batch.batch || batch.uid}
      batchData={batch}
      username={username}
      _index={_index}
      label={label}
      gridColumn="span 6"
      actionButtons={actionButtons}
      as="div"
      onClick={handleClick}
    />
  );
};

const Feedrow = ({
  item,
  index,
  username,
  educatorUID,
  routeToPage,
  isPlatinumEducator,
  topicsSectionRef
}) => {
  const feedData = item || emptyObject;
  const filtersApplied = feedData?.extraBlockInfo?.filtersApplied;
  const isMobile = useIsMobile();
  const { selectedGoal } = useSelectedGoal();
  const { uid, goalSlug } = selectedGoal || {};
  const isSubscribedToGoal = useSelector(getIsSubscribedToPlus(uid));
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(uid));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const goalSegmentData = useGoalEventProps({ goalUID: uid });
  const isSubscribedToGoalInPracticeSection = useMyInfo(
    getIsSubscribedToGoal(feedData.extraBlockInfo?.uid)
  );

  const { dppFeedRowRef } = useScrollToDPPSection(
    feedData.type === DPPS,
    feedData.isFetchingDpps
  );

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const onTopicCardClick = ({ educatorCoveredTopic = true }) => {
    if (!educatorCoveredTopic) return;

    addEducatorInLocalStorage(username);
    segment.track(SYLLABUS_CARD_CLICKED, {
      ...goalSegmentData,
      tab: 'Topics',
      last_primary_source: 'Educator Profile',
      last_primary_source_section: 'Educator Profile'
    });
  };

  if (feedData.type === 'banner')
    return (
      <EducatorLegendsBanner course={feedData.data[0]} label={feedData.type} />
    );

  if (
    isMobile &&
    item.type === 'batch_groups' &&
    feedData?.batchGroups?.length === 1
  ) {
    const batch = feedData.batchGroups[0]; // handling single batch Card based on item type "batch_groups" for mobile devices for platform batch group
    return (
      <SingleBatchCardSection
        label={feedData.label}
        key={batch.uid}
        batch={batch}
        username={username}
        gridColumn="span 6"
        lastPrimarySourceSection="Educator Profile"
      />
    );
  }

  if (
    item.type === TOPICS &&
    (isSubscribedToGoal || isOfflineSubscription) &&
    feedData?.data?.length > 0
  ) {
    return (
      <TopicContainer ref={topicsSectionRef}>
        <StyH4 color="var(--color-text-primary)">{feedData.label}</StyH4>
        <Topics
          data={feedData.data}
          goalSlug={goalSlug}
          goalUID={uid}
          educatorUID={educatorUID}
          educatorUsername={username}
          topicCardClick={onTopicCardClick}
        />
      </TopicContainer>
    );
  }

  let count = feedData?.courses?.length;
  if (!count && feedData?.batches?.length) count = feedData.batches.length; // If the block is a batch block.

  if (!count && feedData?.batchGroups?.length)
    count = feedData.batchGroups.length; // If the block is a platform batch group batch block.

  if (!count && feedData.dpps?.length) count = feedData.dpps.length;

  if (feedData.type === DPPS && !count && !feedData.isFetchingDpps) {
    return null;
  }

  const routeToNewPage = feedData.dpps ? 'practices' : routeToPage;
  const itemsLimit = 4;

  const renderDpp = () => {
    return feedData.dpps?.map((dpp) => (
      // eslint-disable-next-line react/no-array-index-key
      <DppCard
        as={!isLoggedIn && 'div'}
        key={dpp.uid}
        practice={dpp}
        isSubscribed={isSubscribedToGoalInPracticeSection}
        isLoggedIn={isLoggedIn}
        actionButton={
          !isLoggedIn && (
            <Button variant="brand" onClick={openLoginDrawer}>
              Sign up
            </Button>
          )
        }
      />
    ));
  };

  const renderDppSkeleton = () => {
    return new Array(3).fill(0)?.map((dpp, _index) => (
      // eslint-disable-next-line react/no-array-index-key
      <DppCardSkeleton practice={dpp} key={_index} />
    ));
  };

  const onSeeAll = () => {
    if (!!feedData?.extraBlockInfo?.seeAllLink && count > 3) {
      segment.track(SEE_ALL_CLICKED, {
        last_primary_source_section: feedData.type
      });
      if (feedData.type === SpecialClass) {
        pushRoute(
          {
            pathname: '/[authorID]/special-classes/',
            query: {
              goal: filtersApplied?.goal?.id || 'all',
              type: filtersApplied?.filterBy?.id
            }
          },
          `/@${username}/special-classes/?goal=${
            filtersApplied?.goal?.id || 'all'
          }&type=${filtersApplied?.filterBy?.id}`
        );
        return;
      }
      if (
        feedData.type === Batches ||
        feedData.type === PlusGoal ||
        feedData.type === FreeCourses
      ) {
        pushRoute(
          {
            pathname: '/[authorID]/batches-courses/',
            query: {
              goal: filtersApplied?.goal?.id || 'all',
              type: filtersApplied?.filterBy?.id
            }
          },
          `/@${username}/batches-courses/?goal=${
            filtersApplied?.goal?.id || 'all'
          }&type=${filtersApplied?.filterBy?.id}`
        );
        return;
      }
      if (feedData.type === EducatorDpp) {
        pushRoute(
          {
            pathname: '/[authorID]/practices/',
            query: {
              goal: filtersApplied?.goal?.id || 'all',
              type: filtersApplied?.filterBy?.id
            }
          },
          `/@${username}/practices/?goal=${
            filtersApplied?.goal?.id || 'all'
          }&type=${filtersApplied?.filterBy?.id}`
        );
        return;
      }
      pushRoute(
        {
          pathname: `/[authorID]/${routeToNewPage}`,
          query: {
            goal: filtersApplied?.goal?.id || 'all',
            type: filtersApplied?.filterBy?.id
          }
        },
        `/@${username}/${routeToNewPage}/?goal=${
          filtersApplied?.goal?.id || 'all'
        }&type=${filtersApplied?.filterBy?.id}`
      );
    }
  };

  const handleCardClick = () => {
    segmentStorage.setItem(
      'lpss',
      feedData.type === FreeCourses ? 'Free Courses' : 'Courses'
    );
  };

  let itemsPerSlide = 4;
  if (feedData.type === 'educator_dpp') itemsPerSlide = 3;
  if (count <= itemsLimit) itemsPerSlide = count;
  return (
    <CarouselWrapper $isDpp={feedData.dpps} $index={index}>
      <StyledShowCase
        ref={dppFeedRowRef}
        itemsPerSlide={itemsPerSlide}
        title={feedData.label}
        onSeeAll={count > 3 ? onSeeAll : null}
      >
        {feedData.courses?.map((course, _index) => (
          <CardWrapper
            key={course.course || course.uid}
            onClick={handleCardClick}
          >
            {' '}
            {feedData.type === SpecialClass ? (
              <ClassCard
                {...formatClassDetail({
                  classInfo: course,
                  hideDetailsLine: false
                })}
              />
            ) : (
              <CourseCard
                courseUID={course.course || course.uid}
                courseData={course}
                index={_index}
                label={feedData.label}
                username={username}
                gridColumn="span 6"
                disabledTopicGroup={isPlatinumEducator}
                hideLessonCount={isPlatinumEducator}
              />
            )}
          </CardWrapper>
        ))}
        {feedData.batches?.map((batch, _index) => (
          <BatchCard
            key={batch.batch || batch.uid}
            batch={batch}
            username={username}
            label={feedData.label}
            _index={_index}
          />
        ))}

        {feedData.isFetchingDpps ? renderDppSkeleton() : renderDpp()}

        {feedData?.batchGroups?.map((batch, _index) => (
          <StyledPlatformBatch
            key={batch.uid}
            batch={batch}
            username={username}
            _index={_index}
            gridColumn="span 6"
            lastPrimarySourceSection="Educator Profile"
            isFeedCard
          />
        ))}
      </StyledShowCase>
    </CarouselWrapper>
  );
};

export default Feedrow;

Feedrow.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  routeToPage: PropTypes.string
};

Feedrow.defaultProps = {
  routeToPage: 'courses'
};
